// Sauvegarde cloud: la progression locale (localStorage cryptoket.*) est
// répliquée dans la base SQLite du résolveur. Clé = un « code de sauvegarde »
// aléatoire non devinable — copiable pour restaurer sur un autre appareil.

import { RESOLVER_URL } from '../web3/contract'

const KEY_ID = 'cryptoket.saveId'
const KEY_LAST = 'cryptoket.lastCloudTs'
const PREFIX = 'cryptoket.'
// clés purement techniques, inutiles à répliquer
const SKIP = new Set([KEY_ID, KEY_LAST, 'cryptoket.mute', 'cryptoket.musicMute'])

export function saveCode(): string {
  let id = localStorage.getItem(KEY_ID)
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem(KEY_ID, id)
  }
  return id
}

function snapshot(): Record<string, string> {
  const data: Record<string, string> = {}
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i)!
    if (k.startsWith(PREFIX) && !SKIP.has(k)) data[k] = localStorage.getItem(k)!
  }
  return data
}

let pushTimer: number | null = null
let lastPushed = ''

/** Pousse la progression (débouncé — appeler autant qu'on veut). */
export function pushSave(): void {
  if (pushTimer !== null) clearTimeout(pushTimer)
  pushTimer = window.setTimeout(() => {
    pushTimer = null
    const data = snapshot()
    const body = JSON.stringify({ data, ts: Date.now() })
    if (body === lastPushed) return
    void fetch(`${RESOLVER_URL}/save/${saveCode()}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body,
    })
      .then((r) => {
        if (r.ok) {
          lastPushed = body
          localStorage.setItem(KEY_LAST, String(Date.now()))
        }
      })
      .catch(() => {
        // hors-ligne / résolveur indisponible: on retentera au prochain push
      })
  }, 1500)
}

/** Au démarrage: restaure depuis le cloud si le local est plus ancien. */
export async function syncOnBoot(): Promise<void> {
  try {
    const res = await fetch(`${RESOLVER_URL}/save/${saveCode()}`)
    if (res.status === 204) {
      // nouveau joueur: rien côté serveur → on pousse l'état local
      pushSave()
      return
    }
    if (!res.ok) {
      pushSave() // rien au cloud → première réplication
      return
    }
    const remote = (await res.json()) as { data: Record<string, string>; ts: number }
    const localTs = Number(localStorage.getItem(KEY_LAST) ?? 0)
    if (remote.ts > localTs + 2000) {
      for (const [k, v] of Object.entries(remote.data)) {
        if (k.startsWith(PREFIX) && typeof v === 'string') localStorage.setItem(k, v)
      }
      localStorage.setItem(KEY_LAST, String(remote.ts))
      window.location.reload()
    }
  } catch {
    // hors-ligne: le jeu reste 100% jouable en local
  }
}

/** Restaure la progression d'un autre appareil via son code. */
export async function restoreFromCode(code: string): Promise<boolean> {
  const clean = code.trim()
  if (!/^[a-zA-Z0-9-]{16,64}$/.test(clean)) return false
  try {
    const res = await fetch(`${RESOLVER_URL}/save/${clean}`)
    if (!res.ok || res.status === 204) return false // 204: code jamais sauvegardé
    const remote = (await res.json()) as { data: Record<string, string>; ts: number }
    for (const [k, v] of Object.entries(remote.data)) {
      if (k.startsWith(PREFIX) && typeof v === 'string') localStorage.setItem(k, v)
    }
    localStorage.setItem(KEY_ID, clean) // on adopte ce code
    localStorage.setItem(KEY_LAST, String(remote.ts))
    window.location.reload()
    return true
  } catch {
    return false
  }
}
